import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getChannels } from "../../api/ltv/ltv";
import { SessionContext } from "../../context/SessionContext";
import { getCinemaCopy } from "../../statics/cinemaCopy";
import useCarouselScroll from "../../utils/useCarouselScroll";
import CinemaIcon from "./CinemaIcon";
import "./LiveChannelShelf.scss";

function ChannelLogo({ channel }) {
  const logo =
    channel.SecondaryLogoImageUrl || channel.SecondaryLogoUrl || channel.logo;
  const [failed, setFailed] = useState([]);
  useEffect(() => setFailed([]), [logo, channel.logo]);
  const source = [logo, channel.logo].find(
    (url) => url && !failed.includes(url)
  );
  return (
    <span className="cinema-channel-logo" aria-hidden="true">
      {source ? (
        <img
          key={source}
          src={source}
          alt=""
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={() => setFailed((urls) => [...urls, source])}
        />
      ) : (
        <CinemaIcon name="tv" />
      )}
    </span>
  );
}

export default function LiveChannelShelf() {
  const copy = getCinemaCopy();
  const { profile } = useContext(SessionContext);
  const profileId = profile?.id;
  const [channels, setChannels] = useState([]);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const { trackRef, canPrevious, canNext, move } = useCarouselScroll({
    itemCount: channels.length,
    resetKey: profileId
  });

  useEffect(() => {
    let active = true;
    setChannels([]);
    setStatus("loading");
    // Reuse the profile-scoped channel cache and its bounded request timeout.
    getChannels()
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid channel list");
        if (!active) return;
        setChannels(data.filter((channel) => channel?.id));
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [profileId, attempt]);

  if (status === "ready" && !channels.length) return null;

  return (
    <section
      className="cinema-shelf cinema-live-channels"
      aria-label={copy.live}
      aria-busy={status === "loading"}
    >
      <div className="cinema-shelf-heading">
        <h2 className="label-title">{copy.live}</h2>
        {status === "ready" && (
          <div className="cinema-shelf-controls">
            <button
              type="button"
              className="cinema-icon-button"
              aria-label={`${copy.previous}: ${copy.live}`}
              disabled={!canPrevious}
              onClick={() => move(-1)}
            >
              <CinemaIcon name="previous" />
            </button>
            <button
              type="button"
              className="cinema-icon-button"
              aria-label={`${copy.next}: ${copy.live}`}
              disabled={!canNext}
              onClick={() => move(1)}
            >
              <CinemaIcon name="next" />
            </button>
          </div>
        )}
      </div>
      {status === "error" ? (
        <div className="cinema-channel-error">
          <p role="alert">{copy.loadError}</p>
          <button
            type="button"
            className="cinema-button cinema-button-secondary"
            onClick={() => setAttempt((value) => value + 1)}
          >
            {copy.retry}
          </button>
        </div>
      ) : (
        <>
          {status === "loading" && (
            <span className="atlas-status-sr" role="status">
              {copy.loading}
            </span>
          )}
          <div className="cinema-channel-track" ref={trackRef}>
            {status === "loading"
              ? Array.from({ length: 10 }, (_, index) => (
                  <div
                    className="cinema-channel-skeleton"
                    key={index}
                    aria-hidden="true"
                  />
                ))
              : channels.map((channel) => (
                  <Link
                    key={channel.id}
                    className="cinema-channel-card"
                    to={`/dashboard/livetv/${channel.id}`}
                    title={channel.name}
                  >
                    <ChannelLogo channel={channel} />
                    <span className="cinema-channel-name">{channel.name}</span>
                  </Link>
                ))}
          </div>
        </>
      )}
    </section>
  );
}
